import React, { useState, useEffect, useRef, useCallback } from "react";

const App = () => {
  // Common state variables
  const [interestRate, setInterestRate] = useState(8);
  const [term, setTerm] = useState(6); // default term is 6 months
  const [includeOriginationFee, setIncludeOriginationFee] = useState(false);
  const [originationFeePercentage, setOriginationFeePercentage] =
    useState(4.79); // default 4.79%

  // Calculation mode: "loanAmount" or "monthlyPayment"
  const [calcMode, setCalcMode] = useState("loanAmount");

  // State for Loan Amount mode:
  const [loanAmount, setLoanAmount] = useState(10000);
  const [loanDisplay, setLoanDisplay] = useState("$10,000");
  const [interestOnlyPeriod, setInterestOnlyPeriod] = useState(0); // used only in Loan Amount mode

  // State for Monthly Payment mode:
  const [monthlyPaymentInput, setMonthlyPaymentInput] = useState(500);
  const [monthlyPaymentDisplay, setMonthlyPaymentDisplay] = useState(
    new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(500)
  );

  // Monthly interest rate (decimal)
  const r = interestRate / 100 / 12;

  // --- Calculations for Loan Amount mode ---
  let feeLA = includeOriginationFee
    ? loanAmount * (originationFeePercentage / 100)
    : 0;
  let isBalloon = calcMode === "loanAmount" && interestOnlyPeriod === term;
  let financedPrincipalLA =
    calcMode === "loanAmount" && isBalloon && includeOriginationFee
      ? loanAmount
      : loanAmount + feeLA;
  const calculateMonthlyPayment_LA = () => {
    if (isBalloon) return 0;
    const remainingTerm = term - interestOnlyPeriod;
    if (remainingTerm <= 0) return 0;
    return financedPrincipalLA * (r / (1 - Math.pow(1 + r, -remainingTerm)));
  };
  const monthlyPayment_LA =
    calcMode === "loanAmount" ? calculateMonthlyPayment_LA() : 0;
  const interestOnlyInterest = interestOnlyPeriod * (financedPrincipalLA * r);
  const totalRepayment_LA = isBalloon
    ? (includeOriginationFee ? feeLA : 0) +
      (term - (includeOriginationFee ? 1 : 0)) * (financedPrincipalLA * r) +
      (financedPrincipalLA + financedPrincipalLA * r)
    : interestOnlyInterest + monthlyPayment_LA * (term - interestOnlyPeriod);
  const finalBalloonPayment = financedPrincipalLA + financedPrincipalLA * r;

  // --- Calculations for Monthly Payment mode ---
  const computedNominalLoan =
    (monthlyPaymentInput * (1 - Math.pow(1 + r, -term))) / r;
  const feeMP = includeOriginationFee
    ? computedNominalLoan * (originationFeePercentage / 100)
    : 0;
  const effectiveLoanMP = computedNominalLoan - feeMP;
  const totalRepayment_MP = monthlyPaymentInput * term;

  useEffect(() => {
    if (calcMode === "monthlyPayment") {
      setInterestOnlyPeriod(0);
    }
  }, [calcMode]);

  // --- Height Adjustment Logic ---
  const containerRef = useRef(null);

  const sendHeight = useCallback(() => {
    const containerHeight = containerRef.current
      ? containerRef.current.getBoundingClientRect().height
      : 0;
    const docHeight = document.documentElement.scrollHeight;
    const height = Math.max(containerHeight, docHeight);
    parent.postMessage({ iframeHeight: height }, "*");
  }, []);

  useEffect(() => {
    setTimeout(sendHeight, 300);
    sendHeight();
    const resizeObserver = new ResizeObserver(() => {
      sendHeight();
    });
    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }
    const intervalId = setInterval(sendHeight, 150);
    return () => {
      if (containerRef.current) {
        resizeObserver.unobserve(containerRef.current);
      }
      clearInterval(intervalId);
    };
  }, [sendHeight]);

  useEffect(() => {
    sendHeight();
  }, [
    term,
    interestOnlyPeriod,
    calcMode,
    loanAmount,
    monthlyPaymentInput,
    includeOriginationFee,
    originationFeePercentage,
    sendHeight,
  ]);

  const currentLoanAmount =
    calcMode === "loanAmount" ? loanAmount : effectiveLoanMP;
  const currentMonthlyPayment =
    calcMode === "loanAmount" ? monthlyPayment_LA : monthlyPaymentInput;
  const currentTotalRepayment =
    calcMode === "loanAmount" ? totalRepayment_LA : totalRepayment_MP;

  const generateSchedule = () => {
    let schedule = [];
    if (calcMode === "loanAmount") {
      if (isBalloon) {
        for (let i = 1; i <= term; i++) {
          const interestPayment = financedPrincipalLA * r;
          if (i === 1 && includeOriginationFee) {
            schedule.push({
              month: i,
              monthlyPayment: (interestPayment + feeLA).toFixed(2),
              interestPayment: interestPayment.toFixed(2),
              principalPayment: feeLA.toFixed(2),
              remainingBalance: financedPrincipalLA.toFixed(2),
            });
          } else if (i < term) {
            schedule.push({
              month: i,
              monthlyPayment: interestPayment.toFixed(2),
              interestPayment: interestPayment.toFixed(2),
              principalPayment: "-",
              remainingBalance: financedPrincipalLA.toFixed(2),
            });
          } else {
            schedule.push({
              month: i,
              monthlyPayment: (financedPrincipalLA + interestPayment).toFixed(2),
              interestPayment: interestPayment.toFixed(2),
              principalPayment: financedPrincipalLA.toFixed(2),
              remainingBalance: "0.00",
            });
          }
        }
      } else {
        let balance = financedPrincipalLA;
        const mp = calculateMonthlyPayment_LA();
        for (let i = 1; i <= term; i++) {
          const interestPayment = balance * r;
          let principalPayment = 0;
          let paymentToShow = "";
          if (i <= interestOnlyPeriod) {
            paymentToShow = interestPayment.toFixed(2);
          } else {
            principalPayment = mp - interestPayment;
            paymentToShow = mp.toFixed(2);
          }
          balance -= principalPayment;
          if (i === term) balance = 0;
          schedule.push({
            month: i,
            monthlyPayment: paymentToShow,
            interestPayment: interestPayment.toFixed(2),
            principalPayment:
              i <= interestOnlyPeriod ? "-" : principalPayment.toFixed(2),
            remainingBalance: balance.toFixed(2),
          });
        }
      }
    } else {
      let balance = effectiveLoanMP;
      let month = 1;
      while (balance > 0.01 && month <= term) {
        let interestPayment = balance * r;
        let payment, principalPayment;
        if (monthlyPaymentInput > balance + interestPayment) {
          payment = balance + interestPayment;
          principalPayment = balance;
          balance = 0;
        } else {
          payment = monthlyPaymentInput;
          principalPayment = payment - interestPayment;
          balance -= principalPayment;
        }
        schedule.push({
          month,
          monthlyPayment: payment.toFixed(2),
          interestPayment: interestPayment.toFixed(2),
          principalPayment: principalPayment.toFixed(2),
          remainingBalance: balance.toFixed(2),
        });
        month++;
      }
    }
    return schedule;
  };

  const schedule = generateSchedule();

  // --- Helper Functions for Formatting ---
  const formatCurrencyNoDecimals = (value) =>
    new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);

  const formatCurrencyWithDecimals = (value) =>
    new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);

  // --- Handlers for Input ---
  const handleLoanAmountChange = (e) => {
    const raw = e.target.value.replace(/[^0-9]/g, "");
    const numericValue = Number(raw);
    const clampedValue = Math.min(numericValue, 999999999);
    setLoanAmount(clampedValue);
    setLoanDisplay(formatCurrencyNoDecimals(clampedValue));
  };

  const handleLoanBlur = () => {
    setLoanDisplay(formatCurrencyNoDecimals(loanAmount));
  };

  const handleMonthlyPaymentChange = (e) => {
    const raw = e.target.value.replace(/[^0-9]/g, "");
    const numericValue = Number(raw);
    const clampedValue = Math.min(numericValue, 999999);
    setMonthlyPaymentInput(clampedValue);
    setMonthlyPaymentDisplay(formatCurrencyNoDecimals(clampedValue));
  };

  const handleMonthlyPaymentBlur = () => {
    setMonthlyPaymentDisplay(formatCurrencyNoDecimals(monthlyPaymentInput));
  };

  const handleOriginationFeePercentageChange = (e) => {
    setOriginationFeePercentage(Number(e.target.value));
  };

  // --- Slider Layout ---
  const renderSliders = () => {
    if (calcMode === "loanAmount") {
      return (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          <div className="flex flex-col space-y-6">
            <div className="flex flex-col items-center">
              <label className="text-lg font-medium text-gray-700 mb-2">
                Interest Rate (%)
              </label>
              <input
                type="range"
                min="0"
                max="20"
                step="0.1"
                value={interestRate}
                onChange={(e) => setInterestRate(Number(e.target.value))}
                className="w-full"
              />
              <div className="text-gray-800 mt-2 text-sm">{interestRate}%</div>
            </div>
            <div className="flex flex-col items-center">
              <label className="text-lg font-medium text-gray-700 mb-2">
                Interest-Only Period (months)
              </label>
              <input
                type="range"
                min="0"
                max={term}
                step="1"
                value={interestOnlyPeriod}
                onChange={(e) => setInterestOnlyPeriod(Number(e.target.value))}
                className="w-full"
              />
              <div className="text-gray-800 mt-2 text-sm">
                {interestOnlyPeriod} months
              </div>
            </div>
          </div>
          <div className="flex flex-col items-center">
            <div className="flex flex-col items-center">
              <label className="text-lg font-medium text-gray-700 mb-2">
                Term (months)
              </label>
              <input
                type="range"
                min="6"
                max="120"
                step="1"
                value={term}
                onChange={(e) => setTerm(Number(e.target.value))}
                className="w-full"
              />
              <div className="text-gray-800 mt-2 text-sm">{term} months</div>
            </div>
            <div className="flex flex-col items-center">
              <label className="text-lg font-medium text-gray-700 mb-2">
                Origination Fee (%)
              </label>
              <input
                type="range"
                min="0"
                max="10"
                step="0.1"
                value={originationFeePercentage}
                onChange={handleOriginationFeePercentageChange}
                className="w-full"
              />
              <div className="text-gray-800 mt-2 text-sm">
                {originationFeePercentage}%
              </div>
            </div>
          </div>
        </div>
      );
    } else {
      return (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="flex flex-col items-center">
            <label className="text-lg font-medium text-gray-700 mb-2">
              Interest Rate (%)
            </label>
            <input
              type="range"
              min="0"
              max="20"
              step="0.1"
              value={interestRate}
              onChange={(e) => setInterestRate(Number(e.target.value))}
              className="w-full"
            />
            <div className="text-gray-800 mt-2 text-sm">{interestRate}%</div>
          </div>
          <div className="flex flex-col items-center">
            <label className="text-lg font-medium text-gray-700 mb-2">
              Term (months)
            </label>
            <input
              type="range"
              min="6"
              max="120"
              step="1"
              value={term}
              onChange={(e) => setTerm(Number(e.target.value))}
              className="w-full"
            />
            <div className="text-gray-800 mt-2 text-sm">{term} months</div>
          </div>
          <div className="flex flex-col items-center">
            <label className="text-lg font-medium text-gray-700 mb-2">
              Origination Fee (%)
            </label>
            <input
              type="range"
              min="0"
              max="10"
              step="0.1"
              value={originationFeePercentage}
              onChange={handleOriginationFeePercentageChange}
              className="w-full"
            />
            <div className="text-gray-800 mt-2 text-sm">
              {originationFeePercentage}%
            </div>
          </div>
        </div>
      );
    }
  };

  // New: Redirect function for the Start Application button
  const handleRedirect = () => {
    window.open(
      "https://keystonecapitalpartners.ca",
      "_blank",
      "noopener,noreferrer"
    );
  };

  // New: Print function using window.print()
  const handlePrint = () => {
    window.print();
  };

  return (
    <div
      ref={containerRef}
      className="p-6 max-w-4xl mx-auto bg-white shadow-md rounded-lg"
    >
      {/* Global Print Styles */}
      <style>{`
        @media print {
          .schedule-container {
            page-break-before: always;
          }
          /* Optionally hide buttons during printing */
          .no-print {
            display: none;
          }
        }
      `}</style>

      <h2 className="text-3xl font-bold mb-6 text-blue-700 text-center">
        Business Loan Calculator
      </h2>
      <p className="text-gray-600 mb-4 text-center text-lg">
        Calculate your loan payments and track the repayment schedule.
      </p>

      {/* Toggle for Calculation Mode */}
      <div className="mb-6 flex justify-center space-x-4">
        <label className="inline-flex items-center">
          <input
            type="radio"
            className="form-radio"
            value="loanAmount"
            checked={calcMode === "loanAmount"}
            onChange={() => setCalcMode("loanAmount")}
          />
          <span className="ml-2">Calculate by Loan Amount</span>
        </label>
        <label className="inline-flex items-center">
          <input
            type="radio"
            className="form-radio"
            value="monthlyPayment"
            checked={calcMode === "monthlyPayment"}
            onChange={() => setCalcMode("monthlyPayment")}
          />
          <span className="ml-2">Calculate by Monthly Payment</span>
        </label>
      </div>

      {/* Input Section based on Mode */}
      {calcMode === "loanAmount" ? (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          {/* Loan Amount Input */}
          <div className="flex flex-col items-center">
            <span className="text-lg font-medium text-gray-700 mb-2">
              Loan Amount
            </span>
            <div className="border border-gray-300 rounded-lg p-2 w-full text-center">
              <input
                type="text"
                maxLength="12"
                value={loanDisplay}
                onChange={handleLoanAmountChange}
                onBlur={handleLoanBlur}
                className="w-full text-center text-xl"
              />
            </div>
            <div className="mt-2 flex items-center">
              <input
                type="checkbox"
                checked={includeOriginationFee}
                onChange={() =>
                  setIncludeOriginationFee(!includeOriginationFee)
                }
                className="mr-2 h-6 w-6"
              />
              <label className="text-gray-700 text-sm">
                Include Loan Origination Fee
              </label>
            </div>
          </div>
          {/* Repayment Amount */}
          <div className="flex flex-col items-center">
            <span className="text-lg font-medium text-gray-700 mb-2">
              Repayment Amount
            </span>
            <div className="border border-gray-300 rounded-lg p-2 w-full text-center text-xl text-blue-700">
              {formatCurrencyWithDecimals(totalRepayment_LA)}
            </div>
          </div>
          {/* Monthly Payment / Balloon Payment */}
          <div className="flex flex-col items-center">
            <span className="text-lg font-medium text-gray-700 mb-2">
              {isBalloon
                ? "Balloon Payment"
                : interestOnlyPeriod > 0
                ? "Adjusted Payment"
                : "Monthly Payment"}
            </span>
            <div className="border border-gray-300 rounded-lg p-2 w-full text-center text-xl text-blue-700">
              {isBalloon
                ? formatCurrencyWithDecimals(finalBalloonPayment)
                : formatCurrencyWithDecimals(monthlyPayment_LA)}
            </div>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          {/* Monthly Payment Input */}
          <div className="flex flex-col items-center">
            <span className="text-lg font-medium text-gray-700 mb-2">
              Monthly Payment
            </span>
            <div className="border border-gray-300 rounded-lg p-2 w-full text-center">
              <input
                type="text"
                maxLength="8"
                value={monthlyPaymentDisplay}
                onChange={handleMonthlyPaymentChange}
                onBlur={handleMonthlyPaymentBlur}
                className="w-full text-center text-xl"
              />
            </div>
            <div className="mt-2 flex items-center">
              <input
                type="checkbox"
                checked={includeOriginationFee}
                onChange={() =>
                  setIncludeOriginationFee(!includeOriginationFee)
                }
                className="mr-2 h-6 w-6"
              />
              <label className="text-gray-700 text-sm">
                Include Loan Origination Fee
              </label>
            </div>
          </div>
          {/* Repayment Amount */}
          <div className="flex flex-col items-center">
            <span className="text-lg font-medium text-gray-700 mb-2">
              Repayment Amount
            </span>
            <div className="border border-gray-300 rounded-lg p-2 w-full text-center text-xl text-blue-700">
              {formatCurrencyWithDecimals(totalRepayment_MP)}
            </div>
          </div>
          {/* Computed Loan Amount (Label changes if fee is included) */}
          <div className="flex flex-col items-center">
            <span className="text-lg font-medium text-gray-700 mb-2">
              {includeOriginationFee
                ? "Net Loan Amount Available"
                : "Loan Amount Available"}
            </span>
            <div className="border border-gray-300 rounded-lg p-2 w-full text-center text-xl text-blue-700">
              {formatCurrencyWithDecimals(effectiveLoanMP)}
            </div>
          </div>
        </div>
      )}

      {/* Slider Section */}
      {calcMode === "loanAmount" ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          <div className="flex flex-col space-y-6">
            <div className="flex flex-col items-center">
              <label className="text-lg font-medium text-gray-700 mb-2">
                Interest Rate (%)
              </label>
              <input
                type="range"
                min="0"
                max="20"
                step="0.1"
                value={interestRate}
                onChange={(e) => setInterestRate(Number(e.target.value))}
                className="w-full"
              />
              <div className="text-gray-800 mt-2 text-sm">{interestRate}%</div>
            </div>
            <div className="flex flex-col items-center">
              <label className="text-lg font-medium text-gray-700 mb-2">
                Interest-Only Period (months)
              </label>
              <input
                type="range"
                min="0"
                max={term}
                step="1"
                value={interestOnlyPeriod}
                onChange={(e) => setInterestOnlyPeriod(Number(e.target.value))}
                className="w-full"
              />
              <div className="text-gray-800 mt-2 text-sm">
                {interestOnlyPeriod} months
              </div>
            </div>
          </div>
          <div className="flex flex-col space-y-6">
            <div className="flex flex-col items-center">
              <label className="text-lg font-medium text-gray-700 mb-2">
                Term (months)
              </label>
              <input
                type="range"
                min="6"
                max="120"
                step="1"
                value={term}
                onChange={(e) => setTerm(Number(e.target.value))}
                className="w-full"
              />
              <div className="text-gray-800 mt-2 text-sm">{term} months</div>
            </div>
            <div className="flex flex-col items-center">
              <label className="text-lg font-medium text-gray-700 mb-2">
                Origination Fee (%)
              </label>
              <input
                type="range"
                min="0"
                max="10"
                step="0.1"
                value={originationFeePercentage}
                onChange={handleOriginationFeePercentageChange}
                className="w-full"
              />
              <div className="text-gray-800 mt-2 text-sm">
                {originationFeePercentage}%
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="flex flex-col items-center">
            <label className="text-lg font-medium text-gray-700 mb-2">
              Interest Rate (%)
            </label>
            <input
              type="range"
              min="0"
              max="20"
              step="0.1"
              value={interestRate}
              onChange={(e) => setInterestRate(Number(e.target.value))}
              className="w-full"
            />
            <div className="text-gray-800 mt-2 text-sm">{interestRate}%</div>
          </div>
          <div className="flex flex-col items-center">
            <label className="text-lg font-medium text-gray-700 mb-2">
              Term (months)
            </label>
            <input
              type="range"
              min="6"
              max="120"
              step="1"
              value={term}
              onChange={(e) => setTerm(Number(e.target.value))}
              className="w-full"
            />
            <div className="text-gray-800 mt-2 text-sm">{term} months</div>
          </div>
          <div className="flex flex-col items-center">
            <label className="text-lg font-medium text-gray-700 mb-2">
              Origination Fee (%)
            </label>
            <input
              type="range"
              min="0"
              max="10"
              step="0.1"
              value={originationFeePercentage}
              onChange={handleOriginationFeePercentageChange}
              className="w-full"
            />
            <div className="text-gray-800 mt-2 text-sm">
              {originationFeePercentage}%
            </div>
          </div>
        </div>
      )}

      {/* Amortization Schedule Table wrapped in schedule-container */}
      <div className="schedule-container overflow-x-auto">
        <table className="w-full border-collapse border border-gray-300 shadow-lg">
          <thead>
            <tr className="bg-blue-100 text-blue-700">
              <th className="border p-3 text-sm font-semibold">Month</th>
              <th className="border p-3 text-sm font-semibold">
                Monthly Payment
              </th>
              <th className="border p-3 text-sm font-semibold">
                Interest Payment
              </th>
              <th className="border p-3 text-sm font-semibold">
                Principal Payment
              </th>
              <th className="border p-3 text-sm font-semibold">
                Remaining Balance
              </th>
            </tr>
          </thead>
          <tbody>
            {schedule.map((row) => (
              <tr key={row.month}>
                <td className="border p-3 text-center">{row.month}</td>
                <td className="border p-3 text-center">
                  {row.monthlyPayment || "-"}
                </td>
                <td className="border p-3 text-center">
                  ${row.interestPayment}
                </td>
                <td className="border p-3 text-center">
                  {row.principalPayment === "0.00"
                    ? "-"
                    : `$${row.principalPayment}`}
                </td>
                <td className="border p-3 text-center">
                  ${row.remainingBalance}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Buttons Container: Start Application and Print side by side */}
      <div className="mt-6 flex justify-center space-x-4">
        {/* Start Application Button (Green) */}
        <button
          onClick={handleRedirect}
          className="bg-green-600 hover:bg-green-700 text-white font-semibold py-3 px-6 rounded-full text-lg"
        >
          Start Application
        </button>
        {/* Print Button (Blue) */}
        <button
          onClick={handlePrint}
          className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-6 rounded-full text-lg"
        >
          Print Schedule &amp; Details
        </button>
      </div>

      {/* Disclaimer Section */}
      <div className="mt-4 p-2 text-sm text-gray-500">
        <p>
          <strong>Disclaimer:</strong> This loan calculator is the property of
          Keystone Capital Partners Inc. (Keystone) and is provided solely for
          illustrative purposes. It does not constitute financial, legal, or tax
          advice. All calculations are estimates and should not be construed as
          representations of actual loan terms. Linking to this tool is permitted;
          however, embedding it requires the express written consent of Keystone.
          By using this calculator, you confirm your acceptance of these terms and
          agree that Keystone and its affiliates shall not be liable for any losses
          or damages from its use.
        </p>
      </div>

      <div className="mb-6"></div>
    </div>
  );
};

export default App;
